import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import { generateQuestionsBasedOnMaterial, verifyAnswers } from '../../../services/aiExerciseService';  
import VoiceSelector from '../../common/VoiceSelector';
import SpeechButton from '../../common/SpeechButton';
import LearningMaterialContent from '../../LearningMaterial/LearningMaterialContent';
import learningMaterialService, { LearningMaterial } from '../../../services/learningMaterialService';
import { htmlToPlainText } from '../materialConfigUtils';

interface MaterialBasedConfigProps {
  initialConfig?: any;
  onConfigChange: (config: any) => void;
  lessonId: number;
}

const MaterialBasedConfig: React.FC<MaterialBasedConfigProps> = ({ onConfigChange, initialConfig = {}, lessonId }) => {
  const [materials, setMaterials] = useState<LearningMaterial[]>([]);
  const [isLoadingMaterials, setIsLoadingMaterials] = useState(true);
  const [questions, setQuestions] = useState<string[]>(initialConfig.questions || []);
  const [customPrompt, setCustomPrompt] = useState<string>(initialConfig.customPrompt || '');
  const [showMaterialToStudent, setShowMaterialToStudent] = useState<boolean>(initialConfig.showMaterialToStudent ?? false);
  const [selectedMaterialId, setSelectedMaterialId] = useState<number | ''>(initialConfig.materialId ?? '');
  const [questionCount, setQuestionCount] = useState<number>(1);
  const [answers, setAnswers] = useState<string[]>([]);
  const [feedbacks, setFeedbacks] = useState<{ grade: number; feedback: string }[]>([]);
  const [selectedVoiceCode, setSelectedVoiceCode] = useState<string>(initialConfig.voiceCode || 'en-US-JennyNeural');

  useEffect(() => {
    const loadMaterials = async () => {
      setIsLoadingMaterials(true);
      try {
        const data = await learningMaterialService.getMaterialsByLesson(lessonId);
        setMaterials(data);
      } catch (error) {
        console.error('Failed to load learning materials:', error);
      } finally {
        setIsLoadingMaterials(false);
      }
    };

    loadMaterials();
  }, [lessonId]);

  const selectedMaterial = useMemo(
    () => materials.find((material) => material.id === selectedMaterialId),
    [materials, selectedMaterialId],
  );

  const getPlainText = () => {
    if (selectedMaterial) {
      return htmlToPlainText(selectedMaterial.content);
    }

    return '';
  };

  const updateConfig = (updatedQuestions: string[] = questions, overrides: Record<string, unknown> = {}) => {
    onConfigChange({
      exerciseType: 'material_based_ai_assisted',
      materialId: selectedMaterialId || undefined,
      questions: updatedQuestions,
      voiceCode: selectedVoiceCode,
      customPrompt,
      showMaterialToStudent,
      ...overrides,
    });
  };

  const handleAnswerChange = (index: number, value: string) => {
    const updatedAnswers = [...answers];
    updatedAnswers[index] = value;
    setAnswers(updatedAnswers);
  };

  const addQuestion = () => {
    const updatedQuestions = [...questions, ''];
    setQuestions(updatedQuestions);
    setAnswers([...answers, '']);
    updateConfig(updatedQuestions);
  };

  const removeQuestion = (index: number) => {
    const updatedQuestions = questions.filter((_, i) => i !== index);
    const updatedAnswers = answers.filter((_, i) => i !== index);
    const updatedFeedbacks = feedbacks.filter((_, i) => i !== index);
    setQuestions(updatedQuestions);
    setAnswers(updatedAnswers);
    setFeedbacks(updatedFeedbacks);
    updateConfig(updatedQuestions);
  };

  const handleQuestionChange = (index: number, value: string) => {
    const updatedQuestions = [...questions];
    updatedQuestions[index] = value;
    setQuestions(updatedQuestions);
    updateConfig(updatedQuestions);
  };

  const handleCustomPromptChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPrompt = e.target.value;
    setCustomPrompt(newPrompt);
    updateConfig(questions, { customPrompt: newPrompt });
  };

  const generateAIQuestions = async () => {
    if (!selectedMaterial) {
      return;
    }

    try {
      const generatedQuestions = await generateQuestionsBasedOnMaterial(getPlainText(), questionCount, customPrompt);
      const newQuestions = [...questions, ...generatedQuestions.data];
      setQuestions(newQuestions);
      setAnswers([...answers, ...new Array(generatedQuestions.data.length).fill('')]);
      updateConfig(newQuestions);
    } catch (error) {
      console.error('Error generating questions:', error);
    }
  };

  const clearQuestions = () => {
    setQuestions([]);
    setAnswers([]);
    setCustomPrompt('');
    setFeedbacks([]);
    setShowMaterialToStudent(false);
    updateConfig([], { customPrompt: '', showMaterialToStudent: false });
  };

  const evaluateAnswer = async (index: number) => {
    try {
      const questionAnswerPair = [{ question: questions[index], studentAnswer: answers[index] }];
      const response = await verifyAnswers(getPlainText(), customPrompt, questionAnswerPair);
      const newFeedbacks = [...feedbacks];
      newFeedbacks[index] = response.data[0];
      setFeedbacks(newFeedbacks);
    } catch (error) {
      console.error('Error verifying answer:', error);
    }
  };

  const handleVoiceSelect = (voice: { code: string }) => {
    setSelectedVoiceCode(voice.code);
    updateConfig(questions, { voiceCode: voice.code });
  };

  useEffect(() => {
    if (selectedMaterialId === '' && initialConfig.materialId) {
      setSelectedMaterialId(initialConfig.materialId);
    }
  }, [initialConfig.materialId, selectedMaterialId]);

  useEffect(() => {
    updateConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMaterialId, selectedVoiceCode, showMaterialToStudent]);

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Materiaalipohjainen harjoitus
      </Typography>

      <VoiceSelector onSelectVoice={handleVoiceSelect} initialVoiceCode={selectedVoiceCode} />

      <Paper elevation={2} sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Valitse oppitunnin materiaali
        </Typography>

        <FormControl fullWidth sx={{ mt: 1 }}>
          <InputLabel id="material-based-material-select-label">Materiaali</InputLabel>
          <Select
            labelId="material-based-material-select-label"
            value={selectedMaterialId}
            label="Materiaali"
            onChange={(event) => setSelectedMaterialId(event.target.value as number)}
            disabled={isLoadingMaterials}
          >
            {materials.map((material) => (
              <MenuItem key={material.id} value={material.id}>
                {material.title}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {materials.length === 0 && !isLoadingMaterials && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Oppitunnille ei ole vielä luotu materiaalia. Luo materiaali ennen tämän tehtävätyypin tallennusta.
          </Alert>
        )}

        {selectedMaterial && (
          <Box sx={{ mt: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <Typography variant="subtitle1" gutterBottom>
              {selectedMaterial.title}
            </Typography>
            <LearningMaterialContent material={selectedMaterial as any} />
            <SpeechButton id="material-speech" text={getPlainText()} voiceCode={selectedVoiceCode} />
          </Box>
        )}
      </Paper>

      <Box sx={{ mt: 3 }}>
        <Typography variant="h6">Mukautettu kehotus</Typography>
        <TextField
          label="Kirjoita mukautettu kehotus"
          variant="outlined"
          fullWidth
          multiline
          minRows={3}
          value={customPrompt}
          onChange={handleCustomPromptChange}
          sx={{ mt: 1 }}
        />
      </Box>

      <Box sx={{ mt: 3 }}>
        <Typography variant="h6">Materiaalin näyttöasetukset</Typography>
        <FormControlLabel
          control={
            <Checkbox
              checked={showMaterialToStudent}
              onChange={(e) => {
                const newValue = e.target.checked;
                setShowMaterialToStudent(newValue);
                updateConfig(questions, { showMaterialToStudent: newValue });
              }}
            />
          }
          label="Näytä materiaali opiskelijalle"
          sx={{ mt: 1 }}
        />
      </Box>

      <Box sx={{ mt: 3 }}>
        <Typography variant="h6">Kysymykset ja vastaukset</Typography>
        {questions.map((question, index) => (
          <Box key={index} sx={{ mt: 2 }}>
            <Box display="flex" alignItems="center">
              <TextField
                label={`Kysymys ${index + 1}`}
                variant="outlined"
                fullWidth
                value={question}
                onChange={(e) => handleQuestionChange(index, e.target.value)}
              />
              <IconButton onClick={() => removeQuestion(index)} sx={{ ml: 1 }}>
                <DeleteIcon />
              </IconButton>
            </Box>

            <TextField
              label="Vastaus"
              variant="outlined"
              fullWidth
              value={answers[index]}
              onChange={(e) => handleAnswerChange(index, e.target.value)}
              sx={{ mt: 1 }}
            />
            <Button variant="contained" onClick={() => evaluateAnswer(index)} sx={{ mt: 1 }}>
              Tarkista vastaus
            </Button>
            {feedbacks[index] && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body1">Arvosana: {feedbacks[index].grade}</Typography>
                <Typography variant="body2">Palaute: {feedbacks[index].feedback}</Typography>
              </Box>
            )}
            <SpeechButton id={`question-speech-${index}`} text={question} voiceCode={selectedVoiceCode} />
          </Box>          
        ))}
        
        <Button
          variant="outlined"
          startIcon={<AddCircleIcon />}
          onClick={addQuestion}
          sx={{ mt: 2 }}
        >
          Lisää kysymys
        </Button>

        <Box display="flex" alignItems="center" sx={{ mt: 2 }}>
          <TextField
            label="Kysymysten määrä"
            type="number"
            variant="outlined"
            value={questionCount}
            onChange={(e) => setQuestionCount(parseInt(e.target.value, 10))}
            sx={{ width: '120px', mr: 2 }}
          />
          <Button variant="contained" onClick={generateAIQuestions} disabled={!selectedMaterial}>
            Generoi kysymykset tekoälyllä
          </Button>
          <Button
            variant="text"
            startIcon={<ClearAllIcon />}
            onClick={clearQuestions}
            sx={{ ml: 1 }}
          >
            Tyhjennä kysymykset
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default MaterialBasedConfig;
